import { jsx, jsxs } from 'react/jsx-runtime';
import { createRoot } from '../../../../node_modules/react-dom/client.js';
import { findNodeFromEditorLocator, DiffPluginKey, applyDiffToTransaction } from './DiffHandler.js';
import { generateHTMLFromFragment } from '../../content.js';
import { Fragment } from '@tiptap/pm/model';
import { styled } from 'styled-components';
import { useDomContentRect } from '@fabric-app/hooks/useDomContentRect';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { IconDropdown14Minus, IconDropdown14Plus } from '@fabric-app/icons/svg';
import { useClientLayoutEffect } from '@fabric-app/hooks/useClientLayoutEffect';
import { Flex } from '@fabric-app/ui/components/Flex';
import { Button } from '@fabric-app/ui/components/Button';
import { renderAstToHTML } from '../../../code/utils/ast.js';
import { useState, useEffect } from 'react';
import { LowlightWorkerManager } from '../../../code/managers/LowlightWorkerManager.js';
import { flushSync } from 'react-dom';
import { TaskItemComponentStatic } from '../TaskItemComponent.js';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';

const Wrapper = styled.div.withConfig({
  displayName: "DiffDisplay__Wrapper",
  componentId: "sc-cd5331bf-0"
})([
  `position:relative;padding:12px 0;z-index:10;`,
  `{&[data-is-separate='true']{padding-top:43px;}}`
], mediaMobile);
const PreviewContent = styled.div.withConfig({
  displayName: "DiffDisplay__PreviewContent",
  componentId: "sc-cd5331bf-1"
})([
  `--top:0px;--height:0px;z-index:1;user-select:none;&&&{> *{margin-top:0;margin-bottom:0;margin-block-start:0;margin-block-end:0;}}`
]);
const Negative = styled.div.withConfig({
  displayName: "DiffDisplay__Negative",
  componentId: "sc-cd5331bf-2"
})([
  `position:absolute;left:0;right:0;bottom:100%;z-index:-1;background:rgba(255,0,30,0.1);display:flex;`,
  `{flex-direction:column;}`
], mediaMobile);
const NegativeBar = styled.div.withConfig({
  displayName: "DiffDisplay__NegativeBar",
  componentId: "sc-cd5331bf-3"
})([
  `background:rgba(255,0,30,0.1);height:100%;color:rgba(`,
  `,0.5);padding:12px 8.5px;`,
  `{background:rgba(255,0,30,0.2);height:auto;width:100%;padding:8.5px 12px;transform:translateY(-100%);}`
], cssVar["color-text-primary-rgb"], mediaMobile);
const Positive = styled.div.withConfig({
  displayName: "DiffDisplay__Positive",
  componentId: "sc-cd5331bf-4"
})([
  `position:absolute;top:0;left:0;right:0;bottom:0;z-index:-1;background:rgba(0,255,13,0.1);display:flex;`,
  `{flex-direction:column;}`
], mediaMobile);
const PositiveBar = styled.div.withConfig({
  displayName: "DiffDisplay__PositiveBar",
  componentId: "sc-cd5331bf-5"
})([
  `background:rgba(0,255,13,0.1);height:100%;color:rgba(`,
  `,0.5);padding:12px 8.5px;`,
  `{height:auto;width:100%;padding:8.5px 12px;background:rgba(0,255,13,0.2);transform:translateY(-100%);[data-is-separate='true'] &{background:rgba(0,255,13,0.1);transform:unset;}}`
], cssVar["color-text-primary-rgb"], mediaMobile);
const ButtonGroup = styled(Flex).withConfig({
  displayName: "DiffDisplay__ButtonGroup",
  componentId: "sc-cd5331bf-6"
})([
  `display:none;position:absolute;top:100%;right:12px;z-index:10;border-radius:0 0 6px 6px;overflow:hidden;`
]);
const ApproveButton = styled(Button).attrs((p) => {
  return {
    ...p,
    size: "xss"
  };
}).withConfig({
  displayName: "DiffDisplay__ApproveButton",
  componentId: "sc-cd5331bf-7"
})([
  `background:linear-gradient(0deg,rgba(0,242,12,0.5) 0%,rgba(0,242,12,0.5) 100%),#fff;color:rgba(0,0,0,0.7);border-radius:0;`
]);
const RejectButton = styled(Button).attrs((p) => {
  return {
    ...p,
    size: "xss"
  };
}).withConfig({
  displayName: "DiffDisplay__RejectButton",
  componentId: "sc-cd5331bf-8"
})([
  `background:linear-gradient(0deg,rgba(255,0,0,0.5) 0%,rgba(255,0,0,0.5) 100%),#fff;color:rgba(0,0,0,0.6);border-radius:0;`
]);
const HoverContainer = styled.div.withConfig({
  displayName: "DiffDisplay__HoverContainer",
  componentId: "sc-cd5331bf-9"
})([
  `position:absolute;left:0;right:0;bottom:0;z-index:10;&:hover{`,
  `{display:flex;}}`,
  `{`,
  `{display:flex;}}`
], ButtonGroup, mediaMobile, ButtonGroup);
const getAbsoluteBoundingRect = (element, doc) => {
  const offsetX = doc.scrollLeft;
  const offsetY = doc.scrollTop;
  const rect = element.getBoundingClientRect();
  const baseRect = {
    bottom: rect.bottom + offsetY,
    height: rect.height,
    left: rect.left + offsetX,
    right: rect.right + offsetX,
    top: rect.top + offsetY,
    width: rect.width,
    x: rect.left + offsetX,
    y: rect.top + offsetY
  };
  return {
    ...baseRect,
    toJSON: () => baseRect
  };
};
const processAdditionsInHTML = (html, editorDiff) => {
  if (!html) {
    return html;
  }
  const tempContainer = document.createElement("div");
  tempContainer.innerHTML = html;
  if (editorDiff.changeDetails.type === "very_different") {
    const textNodesToWrap = [];
    const collectTextNodesRecursive = (node) => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        if (!(node.parentNode && node.parentNode.nodeName === "SPAN" && node.parentNode.hasAttribute("data-diff-preview-addition"))) {
          textNodesToWrap.push(node);
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.nodeName === "SPAN" && node.hasAttribute("data-diff-preview-addition")) {
          return;
        }
        Array.from(node.childNodes).forEach(collectTextNodesRecursive);
      }
    };
    collectTextNodesRecursive(tempContainer);
    for (const node of textNodesToWrap) {
      const span = document.createElement("span");
      span.setAttribute("data-diff-preview-addition", "true");
      node.parentNode?.insertBefore(span, node);
      span.appendChild(node);
    }
    return tempContainer.innerHTML;
  }
  if (!editorDiff.changeDetails.additions.length) {
    return html;
  }
  const additions = [
    ...editorDiff.changeDetails.additions
  ].sort((a, b) => b.from - a.from);
  for (const addition of additions) {
    const additionStart = addition.from;
    const additionEnd = addition.to;
    const tasks = [];
    let currentCharacterOffset = 0;
    const collectTasksRecursive = (parentNode) => {
      const childNodes = Array.from(parentNode.childNodes);
      for (const child of childNodes) {
        if (child.nodeType === Node.TEXT_NODE) {
          const textNode = child;
          const nodeLength = textNode.textContent?.length || 0;
          const nodeStartOffset = currentCharacterOffset;
          const nodeEndOffset = currentCharacterOffset + nodeLength;
          const overlapGlobalStart = Math.max(nodeStartOffset, additionStart);
          const overlapGlobalEnd = Math.min(nodeEndOffset, additionEnd);
          if (overlapGlobalStart < overlapGlobalEnd) {
            const effectiveStartInNode = overlapGlobalStart - nodeStartOffset;
            const effectiveEndInNode = overlapGlobalEnd - nodeStartOffset;
            tasks.push({
              node: textNode,
              startInNode: effectiveStartInNode,
              endInNode: effectiveEndInNode
            });
          }
          currentCharacterOffset += nodeLength;
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          if (child.nodeName === "SPAN" && child.hasAttribute("data-diff-preview-addition")) {
            currentCharacterOffset += child.textContent?.length || 0;
          } else {
            collectTasksRecursive(child);
          }
        }
      }
    };
    currentCharacterOffset = 0;
    collectTasksRecursive(tempContainer);
    for (let i = tasks.length - 1; i >= 0; i--) {
      const { node, startInNode, endInNode } = tasks[i];
      if (node.parentNode && node.parentNode.nodeName === "SPAN" && node.parentNode.hasAttribute("data-diff-preview-addition")) {
        if (startInNode === 0 && endInNode === (node.textContent?.length || 0)) {
          continue;
        }
      }
      const additionText = node.textContent?.substring(startInNode, endInNode) || "";
      if (!additionText) continue;
      const span = document.createElement("span");
      span.setAttribute("data-diff-preview-addition", "true");
      span.textContent = additionText;
      const parent = node.parentNode;
      if (!parent) continue;
      if (startInNode === 0 && endInNode === (node.textContent?.length || 0)) {
        parent.replaceChild(span, node);
      } else {
        const beforeText = node.textContent?.substring(0, startInNode) || "";
        const afterText = node.textContent?.substring(endInNode) || "";
        const fragment = document.createDocumentFragment();
        if (beforeText) {
          fragment.appendChild(document.createTextNode(beforeText));
        }
        fragment.appendChild(span);
        if (afterText) {
          fragment.appendChild(document.createTextNode(afterText));
        }
        parent.replaceChild(fragment, node);
      }
    }
  }
  return tempContainer.innerHTML;
};
const addCodeHighlighting = async (html, editorDiff) => {
  if (!html) {
    return html;
  }
  const tempContainer = document.createElement("div");
  tempContainer.innerHTML = html;
  const codeElements = tempContainer.querySelectorAll("pre > code");
  if (!codeElements.length) {
    return html;
  }
  console.log("editorDiff", editorDiff);
  const manager = LowlightWorkerManager.getInstance();
  const worker = manager.getWorker();
  const promises = [];
  for (const codeElement of codeElements) {
    const text = codeElement.textContent?.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    if (!text) continue;
    const lang = codeElement.className.match(/language-(\w+)/)?.[1];
    const promise = worker.postMessage("parse", {
      code: text,
      lang
    }, {
      timeout: 1e3 * 20
    }).then((response) => {
      codeElement.innerHTML = processAdditionsInHTML(renderAstToHTML(response.root.children), editorDiff);
    }).catch((error) => {
      console.error(error);
    });
    promises.push(promise);
  }
  await Promise.allSettled(promises);
  return tempContainer.innerHTML;
};
const replaceTaskItems = (html) => {
  const tempContainer = document.createElement("div");
  tempContainer.innerHTML = html;
  const taskItems = tempContainer.querySelectorAll('li[data-type="taskItem"]');
  for (const taskItem of taskItems) {
    const div = document.createElement("div");
    const root = createRoot(div);
    for (const child of taskItem.childNodes) {
      if (child instanceof HTMLInputElement) {
        child.remove();
      } else if (child instanceof HTMLSpanElement && child.textContent?.trim() === "") {
        child.remove();
      }
    }
    flushSync(() => {
      root.render(/* @__PURE__ */ jsx(TaskItemComponentStatic, {
        checked: taskItem.dataset.checked === "true",
        innerHTML: taskItem.innerHTML
      }));
    });
    taskItem.outerHTML = div.innerHTML;
    root.unmount();
  }
  return tempContainer.innerHTML;
};
const DiffDisplay = ({ view, editorDiff, containerRef, previewHTML, targetElement, initialDocRect, initialTargetRect, initialContainerRect }) => {
  const [dynamicDocRect] = useDomContentRect(view.dom, {
    includeRectPosition: true,
    observeParentResize: true,
    debounceDelay: 100
  });
  const docRect = dynamicDocRect ?? initialDocRect;
  const [dynamicTargetRect] = useDomContentRect(targetElement, {
    includeRectPosition: true,
    adjustByScrollOffset: 1
  });
  const targetRect = dynamicTargetRect ?? initialTargetRect;
  const [dynamicContainerRect] = useDomContentRect(containerRef, {
    includeRectPosition: true,
    adjustByScrollOffset: 1
  });
  const containerRect = dynamicContainerRect ?? initialContainerRect;
  useClientLayoutEffect(() => {
  }, [
    editorDiff,
    view
  ]);
  const diffTop = initialTargetRect.bottom - initialContainerRect.top + 12;
  const showContainer = editorDiff.changeDetails.type === "mixed" || editorDiff.changeDetails.type === "very_different";
  const onReject = () => {
    const tr = view.state.tr;
    tr.setMeta(DiffPluginKey, {
      remove: [
        editorDiff.hash
      ]
    });
    view.dispatch(tr);
  };
  const onApprove = () => {
    const tr = view.state.tr;
    tr.setMeta(DiffPluginKey, {
      remove: [
        editorDiff.hash
      ]
    });
    applyDiffToTransaction(view, editorDiff, tr);
    view.dispatch(tr);
  };
  const [processedHTML, setProcessedHTML] = useState(previewHTML);
  useEffect(() => {
    const processHTML = async () => {
      const processedHTML2 = await addCodeHighlighting(previewHTML, editorDiff);
      setProcessedHTML(processedHTML2);
    };
    processHTML();
  }, [
    previewHTML,
    editorDiff
  ]);
  return /* @__PURE__ */ jsxs(Wrapper, {
    "data-is-separate": editorDiff.changeDetails.type === "mixed" || editorDiff.changeDetails.type === "very_different",
    style: {
      marginTop: diffTop,
      marginBottom: Math.abs(diffTop),
      padding: !showContainer ? 0 : void 0
    },
    children: [
      showContainer && /* @__PURE__ */ jsx(PreviewContent, {
        dangerouslySetInnerHTML: {
          __html: processedHTML
        }
      }),
      targetRect && docRect && editorDiff.changeDetails.type !== "additions_only" && /* @__PURE__ */ jsx(Negative, {
        style: {
          height: targetRect.height + 24,
          left: docRect.left - targetRect.left,
          right: targetRect.right - docRect.right
        },
        children: /* @__PURE__ */ jsx(NegativeBar, {
          children: /* @__PURE__ */ jsx(IconDropdown14Minus, {})
        })
      }),
      docRect && containerRect && targetRect && editorDiff.changeDetails.type !== "deletions_only" && /* @__PURE__ */ jsx(Positive, {
        style: editorDiff.changeDetails.type === "additions_only" ? {
          height: targetRect.height + 24,
          left: docRect.left - targetRect.left,
          right: targetRect.right - docRect.right,
          top: "unset",
          bottom: "100%"
        } : {
          left: docRect.left - containerRect.left,
          right: containerRect.right - docRect.right
        },
        children: /* @__PURE__ */ jsx(PositiveBar, {
          children: /* @__PURE__ */ jsx(IconDropdown14Plus, {})
        })
      }),
      targetRect && containerRect && docRect && /* @__PURE__ */ jsx(HoverContainer, {
        style: {
          left: docRect.left - targetRect.left,
          right: targetRect.right - docRect.right,
          top: -targetRect.height - 24
        },
        children: /* @__PURE__ */ jsxs(ButtonGroup, {
          children: [
            /* @__PURE__ */ jsx(ApproveButton, {
              onClick: onApprove,
              children: "Accept change"
            }),
            /* @__PURE__ */ jsx(RejectButton, {
              onClick: onReject,
              children: "Reject change"
            })
          ]
        })
      })
    ]
  });
};
const renderDiffDisplay = (editorDiff, zIndex = 0) => (view, getPos) => {
  const container = document.createElement("div");
  container.style.marginTop = "0";
  container.style.marginBottom = "38px";
  container.style.zIndex = zIndex.toString();
  container.style.position = "relative";
  container.dataset.diffDisplay = "true";
  const root = createRoot(container);
  const result = findNodeFromEditorLocator(view.state.doc, editorDiff.locator);
  if (!result) {
    return container;
  }
  const { node, pos } = result;
  const html = generateHTMLFromFragment(editorDiff.isSelf ? editorDiff.replaceContent : Fragment.from(node.copy(editorDiff.replaceContent)), view.state.schema);
  const processedHTML = replaceTaskItems(processAdditionsInHTML(html, editorDiff));
  let targetElement = view.domAtPos(pos + 1).node;
  if (targetElement.nodeName === "CODE" && targetElement.parentElement && targetElement.parentElement.nodeName === "PRE") {
    targetElement = targetElement.parentElement;
  }
  if (!targetElement || !(targetElement instanceof HTMLElement)) {
    return container;
  }
  const initialDocRect = view.dom.getBoundingClientRect();
  const initialTargetRect = getAbsoluteBoundingRect(targetElement, view.dom);
  container.innerHTML = processedHTML;
  const rawContainerRect = getAbsoluteBoundingRect(container, view.dom);
  const initialContainerRect = {
    top: initialTargetRect.bottom,
    left: initialTargetRect.left,
    right: initialTargetRect.right,
    bottom: initialTargetRect.bottom + rawContainerRect.height,
    height: rawContainerRect.height,
    width: rawContainerRect.width,
    x: initialTargetRect.x,
    y: initialTargetRect.y,
    toJSON: rawContainerRect.toJSON
  };
  root.render(/* @__PURE__ */ jsx(DiffDisplay, {
    view,
    getPos,
    editorDiff,
    previewHTML: processedHTML,
    containerRef: container,
    targetElement,
    initialDocRect,
    initialTargetRect,
    initialContainerRect
  }));
  container.reactRoot = root;
  return container;
};

export { renderDiffDisplay };
//# sourceMappingURL=DiffDisplay.js.map
